import { and, count, eq, list, type PowerSyncDrizzleDB, sql, todo } from "@famidex/powersync";
import { liveListFilters, liveTodoFilters } from "./filters";

/**
 * Búsqueda global viva (paleta de comandos ⌘K). Antes vivía inline en
 * `command-palette.tsx` con `.from(todo)`/`.from(list)` SIN filtro de papelera
 * → la búsqueda mostraba tareas/listas borradas (bug de invariante disperso).
 * Migrada a la capa live: ahora compone `liveTodoFilters()`/`liveListFilters()`.
 */
export type SearchFilter = "all" | "todo" | "list";

export interface GlobalSearchOptions {
  query: string;
  filter: SearchFilter;
  limit?: number;
}

const DEFAULT_SEARCH_LIMIT = 30;

export function getGlobalSearchQuery(
  db: PowerSyncDrizzleDB,
  { query, filter, limit = DEFAULT_SEARCH_LIMIT }: GlobalSearchOptions,
) {
  const escaped = `%${query}%`;

  const todoQ = db
    .select({
      type: sql<"todo" | "list">`'todo'`.as("type"),
      id: todo.id,
      text: todo.text,
      completed: todo.completed,
      color: sql<string | null>`NULL`.as("color"),
      icon: sql<string | null>`NULL`.as("icon"),
      listId: todo.list_id,
    })
    .from(todo)
    .where(and(sql`${todo.text} LIKE ${escaped}`, ...liveTodoFilters()));

  const listQ = db
    .select({
      type: sql<"todo" | "list">`'list'`.as("type"),
      id: list.id,
      text: list.name,
      completed: sql<number | null>`NULL`.as("completed"),
      color: list.color,
      icon: list.icon,
      listId: sql<string | null>`NULL`.as("list_id"),
    })
    .from(list)
    .where(and(sql`${list.name} LIKE ${escaped}`, ...liveListFilters()));

  if (filter === "todo") {
    return db.select().from(todoQ.as("search_results")).limit(limit);
  }
  if (filter === "list") {
    return db.select().from(listQ.as("search_results")).limit(limit);
  }
  return db.select().from(todoQ.unionAll(listQ).as("search_results")).limit(limit);
}

/**
 * Cuenta las tareas VIVAS de un usuario. Alimenta `checkUnsyncedActor` (prompt
 * «¿conservar datos locales?» al migrar de invitado a cuenta real): antes hacía
 * `count() from(todo)` crudo, contando también las de la papelera. Vía la capa
 * live sólo cuenta vivas — coherente con el resto de vistas. (El merge
 * invitado→real corre igualmente sin condición, así que ninguna fila se pierde;
 * este contador sólo decide si se MUESTRA el prompt.)
 */
export function getLiveTodoCountForUserQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({ total: count() })
    .from(todo)
    .where(and(eq(todo.user_id, userId), ...liveTodoFilters()));
}
